/**
 * Cart loader spinner
 * -------------------
 * Shows a loading overlay every time the cart is being mutated *or* refetched
 * — whether the request is triggered by the theme, the GWP module, or any
 * other script. Two existing overlays are used as hosts:
 *
 *   - `.header-cart-loading` — the in-drawer overlay (rendered inside
 *     `#header-cart` by `snippets/header-cart.liquid`). Visible only when the
 *     cart drawer is open.
 *   - `.page-loading`        — the full-viewport overlay defined in
 *     `layout/theme.liquid`. Used on the cart page (where `#cart-content`
 *     exists).
 *
 * The theme's source CSS expects a `load.gif` asset to render the spinner
 * glyph but that file is missing from `assets/`, so previously the overlays
 * appeared only as a faint white wash with no visible spinner. This script
 * injects a pure-CSS spinner via a `::after` pseudo-element so the overlay
 * shows a spinning indicator regardless of `load.gif`.
 *
 * Trigger model:
 *   - `window.fetch` and `axios.{post,get}` are wrapped. When a cart-related
 *     URL is detected we increment an in-flight counter and add `.active`.
 *   - Cart WRITES (`cart/add|change|update|clear.js`) AND cart READS that
 *     are part of a refresh flow (`cart.js`, `cart?view=json`,
 *     `cart?view=jsonpage`) both keep the loader on. This is critical: the
 *     theme's `showHeaderCart({update:true})` rebuilds the drawer by
 *     refetching cart data *after* the write resolves, and without covering
 *     those reads the spinner would disappear mid-refresh.
 *
 * Debounced hide:
 *   - When the counter hits zero we delay actually removing `.active` by
 *     250 ms. The theme typically chains a cart read immediately after the
 *     write resolves; the delay lets the next call cancel the hide so the
 *     user sees one continuous spinner instead of a flicker.
 *
 * Load order:
 *   - Must load BEFORE `assets/gwp.js`. GWP captures `window.fetch` at boot
 *     time and uses that reference for its internal cart writes — wrapping
 *     fetch first means those writes also drive the loader.
 */
(function () {
  "use strict";

  // --------------------------------------------------------------------
  // Pure-CSS spinner styles
  //
  // Overrides the legacy `background-image: url(load.gif)` rule (asset is
  // missing) with a `::after` pseudo-element animation. Also adds
  // `position: relative` to `.page-loading` so the pseudo-element centers
  // correctly — `.header-cart-loading` is already absolutely positioned.
  // --------------------------------------------------------------------
  var STYLE_ID = "cart-loader-styles";
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      ".header-cart-loading,.page-loading{background-image:none !important;}",
      ".page-loading{position:fixed;}",
      ".header-cart-loading.active,.page-loading.active{display:block;pointer-events:auto;}",
      ".header-cart-loading.active::after,.page-loading.active::after{",
      "  content:'';position:absolute;top:50%;left:50%;width:44px;height:44px;",
      "  margin:-22px 0 0 -22px;border-radius:50%;",
      "  border:3px solid rgba(0,0,0,0.15);",
      "  border-top-color:var(--blue,#0033a0);",
      "  animation:cart-loader-spin .8s linear infinite;",
      "  pointer-events:none;",
      "}",
      "@keyframes cart-loader-spin{to{transform:rotate(360deg);}}"
    ].join("");
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.type = "text/css";
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  }
  injectStyles();

  // --------------------------------------------------------------------
  // In-flight tracking + debounced hide
  // --------------------------------------------------------------------
  var active = 0;
  var hideTimer = null;
  var HIDE_DELAY_MS = 250;

  function isCartPage() {
    return !!document.getElementById("cart-content");
  }

  function applyActive(on) {
    document.querySelectorAll(".header-cart-loading").forEach(function (node) {
      node.classList.toggle("active", on);
    });
    var pageLoader = document.querySelector(".page-loading");
    if (pageLoader) {
      if (on && isCartPage()) pageLoader.classList.add("active");
      else pageLoader.classList.remove("active");
    }
  }

  function showLoaders() {
    active += 1;
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    applyActive(true);
  }

  function hideLoaders() {
    active = Math.max(0, active - 1);
    if (active !== 0) return;
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      hideTimer = null;
      if (active === 0) applyActive(false);
    }, HIDE_DELAY_MS);
  }

  // --------------------------------------------------------------------
  // URL matchers
  //
  // Cart WRITE endpoints: cart/add.js, cart/change.js, cart/update.js,
  // cart/clear.js. Cart READ endpoints used by theme to repaint UI:
  // cart.js, cart?view=json, cart?view=jsonpage. The (^|\/) anchor makes
  // the match safe against locale-prefixed paths like /en-us/cart/add.js.
  // --------------------------------------------------------------------
  function isCartWriteUrl(url) {
    return /(?:^|\/)cart\/(?:add|change|update|clear)\.js(?:\?|$)/i.test(url);
  }
  function isCartReadUrl(url) {
    return (
      /(?:^|\/)cart\.js(?:\?|$)/i.test(url) ||
      /(?:^|\/)cart\?view=/i.test(url) ||
      // Section Rendering API repaint of the cart drawer, e.g.
      // `GET /?sections=cart-drawer`. Without this the spinner's 250 ms
      // debounced hide would fire mid-refetch.
      /[?&]sections=/i.test(url)
    );
  }
  function isCartUrl(url) {
    var s = String(url || "");
    return isCartWriteUrl(s) || isCartReadUrl(s);
  }

  function urlOf(input) {
    if (typeof input === "string") return input;
    if (input && typeof input === "object" && input.url) return String(input.url);
    return "";
  }

  // --------------------------------------------------------------------
  // window.fetch wrapper
  // --------------------------------------------------------------------
  var originalFetch = window.fetch ? window.fetch.bind(window) : null;
  if (originalFetch) {
    window.fetch = function (input, init) {
      if (!isCartUrl(urlOf(input))) return originalFetch(input, init);
      showLoaders();
      var promise = originalFetch(input, init);
      promise.then(hideLoaders, hideLoaders);
      return promise;
    };
  }

  // --------------------------------------------------------------------
  // axios wrapper — `axios.post` is used by theme for cart writes,
  // `axios.get` is used for cart reads inside `showHeaderCart` and
  // `refreshCartPage`. We wrap both at the instance level (not via
  // interceptors) so the loader stays visible until the response promise
  // — and anything chained onto it, like the GWP reconcile — has settled.
  //
  // axios is loaded from `vendor.min.js` (no `defer`), so by the time this
  // script runs `window.axios` is usually ready. The polling fallback
  // handles edge cases where the script order is reversed.
  // --------------------------------------------------------------------
  function wrapAxios() {
    var ax = window.axios;
    if (!ax || ax.__cartLoaderWrapped) return !!ax;
    if (typeof ax.post !== "function" || typeof ax.get !== "function") return false;

    var originalPost = ax.post.bind(ax);
    ax.post = function (url, data, config) {
      if (!isCartUrl(url)) return originalPost(url, data, config);
      showLoaders();
      var promise = originalPost(url, data, config);
      promise.then(hideLoaders, hideLoaders);
      return promise;
    };

    var originalGet = ax.get.bind(ax);
    ax.get = function (url, config) {
      if (!isCartUrl(url)) return originalGet(url, config);
      showLoaders();
      var promise = originalGet(url, config);
      promise.then(hideLoaders, hideLoaders);
      return promise;
    };

    ax.__cartLoaderWrapped = true;
    return true;
  }

  if (!wrapAxios()) {
    var tries = 0;
    var interval = setInterval(function () {
      if (wrapAxios() || ++tries > 50) clearInterval(interval);
    }, 100);
  }
})();
